(() => {
  'use strict';
  if (customElements.get('lm-product-carousel')) return;

  const lifestyleMotion = (() => {
    const roots = new Map();
    const targets = new WeakMap();
    const visible = new Set();
    let observer, timer;
    const identity = entry => entry.root.isConnected && entry.root.contains(entry.media) && entry.template.isConnected && entry.card.dataset.productId === entry.template.dataset.productId && entry.card.dataset.variantId === entry.template.dataset.variantId && entry.main.dataset.showcaseMainImageId === entry.template.dataset.mainImageId;
    const inView = entry => {
      const box = entry.media.getBoundingClientRect(), rail = entry.row.rail.getBoundingClientRect();
      return box.width > 0 && box.height > 0 && box.bottom > Math.max(0, rail.top) && box.top < Math.min(innerHeight, rail.bottom) && box.right > Math.max(0, rail.left) && box.left < Math.min(innerWidth, rail.right);
    };
    const allowed = entry => !entry.failed && identity(entry) && entry.intersecting && entry.owner.canRun(entry.row) && !navigator.connection?.saveData && inView(entry) && entry.main.complete && entry.main.naturalWidth > 0 && !entry.main.hidden;
    const reset = entry => {
      entry.active = false;
      entry.due = null;
      entry.media.removeAttribute('data-lifestyle-running');
      entry.media.removeAttribute('data-lifestyle-active');
    };
    const schedule = () => {
      clearTimeout(timer);
      timer = null;
      let next = Infinity;
      visible.forEach(entry => { if (entry.active && entry.image && entry.due != null) next = Math.min(next, entry.due); });
      if (Number.isFinite(next)) timer = setTimeout(tick, Math.max(0, next - performance.now()));
    };
    const syncEntry = entry => {
      if (!allowed(entry)) { reset(entry); return; }
      if (!entry.active) {
        entry.active = true;
        entry.media.setAttribute('data-lifestyle-running', '');
        entry.due = entry.image ? performance.now() + 2000 : null;
      }
      if (entry.image) {
        if (!entry.image.isConnected) entry.media.append(entry.image);
        if (entry.note && !entry.note.isConnected) entry.media.append(entry.note);
        return;
      }
      if (entry.loading) return;
      const source = entry.template.content.querySelector('img[data-showcase-lifestyle-overlay]');
      if (!source) { entry.failed = true; reset(entry); return; }
      const image = document.importNode(source, true);
      entry.loading = true;
      const fail = () => {
        if (roots.get(entry.root) !== entry.owner || entry.failed) return;
        entry.failed = true;
        entry.loading = false;
        reset(entry);
        entry.image?.remove();
        entry.note?.remove();
        image.remove();
        schedule();
      };
      image.addEventListener('error', fail, { once: true, signal: entry.root.events.signal });
      image.loading = 'eager';
      const decoded = typeof image.decode === 'function' ? image.decode() : new Promise((resolve, reject) => {
        if (image.complete) { image.naturalWidth ? resolve() : reject(); return; }
        image.addEventListener('load', resolve, { once: true });
        image.addEventListener('error', reject, { once: true });
      });
      decoded.then(() => {
        if (roots.get(entry.root) !== entry.owner || entry.failed || !identity(entry)) return;
        if (!image.naturalWidth) throw new Error('Lifestyle image unavailable');
        entry.image = image;
        entry.note = entry.template.content.querySelector('[data-showcase-lifestyle-disclosure]')?.cloneNode(true);
        entry.loading = false;
        if (allowed(entry)) {
          entry.media.append(image);
          if (entry.note) entry.media.append(entry.note);
          entry.due = performance.now() + 2000;
        } else reset(entry);
        schedule();
      }).catch(fail);
    };
    function tick() {
      timer = null;
      const now = performance.now();
      visible.forEach(entry => {
        syncEntry(entry);
        if (entry.active && entry.image && entry.due != null && now >= entry.due) {
          entry.media.toggleAttribute('data-lifestyle-active');
          entry.due = now + 2000;
        }
      });
      schedule();
    }
    const unregister = root => {
      const owner = roots.get(root);
      if (!owner) return;
      roots.delete(root);
      owner.entries.forEach(entry => {
        observer?.unobserve(entry.media);
        targets.delete(entry.media);
        visible.delete(entry);
        reset(entry);
        entry.image?.remove();
        entry.note?.remove();
      });
      schedule();
    };
    return {
      register(root, rows, canRun) {
        unregister(root);
        root.querySelectorAll('[data-showcase-lifestyle-overlay], [data-showcase-lifestyle-disclosure]').forEach(node => node.remove());
        root.querySelectorAll('[data-lifestyle-running], [data-lifestyle-active]').forEach(media => { media.removeAttribute('data-lifestyle-running'); media.removeAttribute('data-lifestyle-active'); });
        if (typeof IntersectionObserver === 'undefined') return;
        observer ||= new IntersectionObserver(entries => {
          entries.forEach(observed => {
            const entry = targets.get(observed.target);
            if (!entry) return;
            entry.intersecting = observed.isIntersecting && observed.intersectionRatio > 0;
            if (entry.intersecting) visible.add(entry); else visible.delete(entry);
            syncEntry(entry);
          });
          schedule();
        }, { rootMargin: '0px', threshold: [0, 0.01] });
        const owner = { entries: [], canRun };
        roots.set(root, owner);
        rows.forEach(row => row.track.querySelectorAll('[data-showcase-lifestyle-frame]').forEach(template => {
          const media = template.closest('.lm-showcase__media');
          const card = template.closest('[data-showcase-card]');
          const main = media?.querySelector('img.lm-showcase__image');
          if (!media || !card || !main || !template.dataset.lifestyleMediaId || !template.dataset.mainImageId) return;
          const entry = { root, owner, row, media, card, main, template, intersecting: false, active: false, due: null };
          if (!identity(entry)) return;
          owner.entries.push(entry);
          targets.set(media, entry);
          observer.observe(media);
          main.addEventListener('load', () => { if (roots.get(root) === owner) { syncEntry(entry); schedule(); } }, { once: true, signal: root.events.signal });
          main.addEventListener('error', () => {
            if (roots.get(root) !== owner) return;
            entry.failed = true;
            reset(entry);
            entry.image?.remove();
            entry.note?.remove();
            schedule();
          }, { once: true, signal: root.events.signal });
        }));
      },
      has(root) { return Boolean(roots.get(root)?.entries.some(entry => !entry.failed)); },
      sync(root) {
        roots.get(root)?.entries.forEach(entry => { if (entry.intersecting || entry.active) syncEntry(entry); });
        schedule();
      },
      unregister
    };
  })();

  class CollectionPass {
    constructor(root) { this.root = root; }
    connect() {
      if (this.ready) return;
      this.rows = [...this.root.querySelectorAll('[data-carousel-row]')].map(container => {
        const rail = container.querySelector('[data-carousel-rail]');
        const track = rail?.querySelector('[data-carousel-track]');
        return rail && track ? { container, rail, track, items: [...track.querySelectorAll('[data-carousel-item]')], previous: container.querySelector('[data-carousel-prev]'), next: container.querySelector('[data-carousel-next]'), progress: container.querySelector('[data-carousel-progress]'), seen: new Set(), activeMs: 0, passActiveMs: 0, distancePx: 0, strides: 0, offset: 0, accountedTime: 0, firstPassComplete: false } : null;
      }).filter(row => row?.items.length);
      if (!this.rows.length) return;
      this.ready = true;
      this.root.ready = true;
      this.root.childObserver?.disconnect();
      this.root.childEvents?.abort();
      clearTimeout(this.root.childTimeout);
      this.events = new AbortController();
      this.root.events = this.events;
      const signal = this.events.signal;
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.button = this.root.querySelector('[data-carousel-motion]');
      this.controls = this.root.querySelector('[data-motion-controls]');
      this.userPaused = this.motion.matches || Boolean(navigator.connection?.saveData);
      this.editor = Boolean(window.Shopify?.designMode);
      this.canAnimate = this.rows.every(row => typeof row.track.animate === 'function');
      this.focused = false;
      this.pageActive = true;
      this.firstPassComplete = false;
      this.passGeneration = 0;
      this.rows.forEach(row => {
        row.container.querySelector('[data-carousel-controls]').hidden = false;
        row.previous?.addEventListener('click', () => this.move(row, -1), { signal });
        row.next?.addEventListener('click', () => this.move(row, 1), { signal });
        row.rail.addEventListener('keydown', event => {
          if (event.target !== row.rail || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
          event.preventDefault();
          this.yieldManual(row);
          if (event.key === 'Home' || event.key === 'End') row.rail.scrollTo({ left: event.key === 'Home' ? 0 : row.rail.scrollWidth, behavior: 'instant' });
          else row.rail.scrollBy({ left: (event.key === 'ArrowRight' ? 1 : -1) * row.columns * row.step, behavior: this.motion.matches ? 'instant' : 'smooth' });
          this.updateControls();
        }, { signal });
        row.rail.addEventListener('scroll', () => {
          const interaction = this.interaction;
          if (interaction?.row === row && !interaction.vertical) {
            if (!interaction.horizontal && Math.abs(row.rail.scrollLeft - interaction.startLeft) > 1) this.yieldManual(row);
            if (interaction.horizontal) this.settleInteraction();
          }
          this.updateControls();
        }, { passive: true, signal });
        row.container.addEventListener('pointerenter', event => { if (event.pointerType === 'mouse') { row.hovered = true; this.sync(); } }, { signal });
        row.container.addEventListener('pointerleave', () => { row.hovered = false; this.sync(); }, { signal });
        row.container.addEventListener('pointerdown', event => {
          if (event.isPrimary === false || event.button !== 0) return;
          this.clearInteraction();
          this.interaction = { row, pointerId: event.pointerId, pointerType: event.pointerType, x: event.clientX, y: event.clientY, startLeft: row.rail.scrollLeft, active: true };
          this.sync();
        }, { passive: true, signal });
        row.rail.addEventListener('wheel', event => {
          if (Math.abs(event.deltaX) <= Math.abs(event.deltaY) && !event.shiftKey) return;
          this.yieldManual(row);
        }, { passive: true, signal });
      });
      this.root.querySelectorAll('.lm-showcase__media img').forEach(image => this.root.prepareImage(image));
      this.root.addEventListener('focusin', event => {
        cancelAnimationFrame(this.blurFrame);
        this.blurFrame = null;
        this.focused = this.focused || !event.target.closest('[data-carousel-motion]');
        if (this.focused) { this.commitAll(); this.manualDirty = true; this.revealFocus(event.target); }
        this.sync();
      }, { signal });
      this.root.addEventListener('focusout', event => {
        cancelAnimationFrame(this.blurFrame);
        this.blurFrame = null;
        if (event.relatedTarget && this.root.contains(event.relatedTarget)) return;
        this.blurFrame = requestAnimationFrame(() => {
          this.blurFrame = null;
          if (!this.ready) return;
          this.focused = this.root.contains(document.activeElement) && (this.focused || document.activeElement !== this.button);
          this.sync();
        });
      }, { signal });
      window.addEventListener('pointermove', event => {
        const interaction = this.interaction;
        if (!interaction?.active || interaction.pointerId !== event.pointerId || interaction.horizontal || interaction.vertical) return;
        const dx = Math.abs(event.clientX - interaction.x), dy = Math.abs(event.clientY - interaction.y);
        if (Math.max(dx, dy) < 8) return;
        if (dx > dy) this.yieldManual(interaction.row);
        else { interaction.vertical = true; this.sync(); }
      }, { passive: true, signal });
      const release = event => {
        if (this.interaction?.pointerId !== event.pointerId) return;
        if (event.type === 'pointercancel' && this.interaction.pointerType === 'touch' && !this.interaction.vertical) return;
        this.interaction.active = false;
        if (this.interaction.vertical) { this.clearInteraction(); this.sync(); }
        else this.settleInteraction();
      };
      window.addEventListener('pointerup', release, { passive: true, signal });
      window.addEventListener('pointercancel', release, { passive: true, signal });
      const releaseTouch = event => {
        if (this.interaction?.pointerType !== 'touch' || event.touches.length) return;
        this.interaction.active = false;
        if (this.interaction.vertical) { this.clearInteraction(); this.sync(); }
        else this.settleInteraction();
      };
      window.addEventListener('touchend', releaseTouch, { passive: true, signal });
      window.addEventListener('touchcancel', releaseTouch, { passive: true, signal });
      this.button?.addEventListener('click', () => { if (this.motion.matches || this.editor) return; this.userPaused = !this.userPaused; this.focused = false; this.sync(); }, { signal });
      this.motion.addEventListener('change', () => {
        if (this.motion.matches) { this.userPaused = true; this.commitAll(); }
        this.sync();
      }, { signal });
      document.addEventListener('visibilitychange', () => this.sync(), { signal });
      window.addEventListener('pagehide', () => { this.pageActive = false; this.clearInteraction(); this.rows.forEach(row => { row.hovered = false; }); this.sync(); }, { signal });
      window.addEventListener('pageshow', () => { this.pageActive = true; this.sync(); }, { signal });
      window.addEventListener('scroll', () => this.scheduleVisibility(), { passive: true, signal });
      window.addEventListener('resize', () => this.layout(), { passive: true, signal });
      if (typeof ResizeObserver !== 'undefined') {
        this.resize = new ResizeObserver(() => this.layout());
        this.rows.forEach(row => this.resize.observe(row.rail));
      }
      if (typeof IntersectionObserver !== 'undefined') {
        this.visibility = new IntersectionObserver(() => this.scheduleVisibility(), { threshold: [0, 0.01, 1] });
        this.rows.forEach(row => this.visibility.observe(row.container));
      }
      this.dialog = new MutationObserver(records => { if (records.some(record => record.target.matches('dialog[data-preview-dialog]'))) this.sync(); });
      this.dialog.observe(document.documentElement, { subtree: true, attributes: true, attributeFilter: ['open'] });
      lifestyleMotion.register(this.root, this.rows, row => this.ready && this.baseState() === 'running' && !row.hovered);
      this.layout();
    }
    disconnect() {
      lifestyleMotion.unregister(this.root);
      this.commitAll();
      this.ready = false;
      this.events?.abort();
      this.clearInteraction();
      this.resize?.disconnect();
      this.visibility?.disconnect();
      this.dialog?.disconnect();
      cancelAnimationFrame(this.visibilityFrame);
      cancelAnimationFrame(this.blurFrame);
      this.blurFrame = null;
      this.root.ready = false;
    }
    layout() {
      if (!this.ready) return;
      const metrics = this.rows.map(row => {
        const width = row.items[0].getBoundingClientRect().width;
        const gap = parseFloat(getComputedStyle(row.track).columnGap) || 0;
        return { width, gap, columns: Math.max(1, Math.floor((row.rail.clientWidth + gap + 1) / (width + gap))) };
      });
      const changed = metrics.some((value, i) => Math.abs(value.width - (this.rows[i].width || 0)) > 0.5 || value.columns !== this.rows[i].columns);
      if (!changed) { this.sync(); return; }
      this.commitAll();
      const maxSlots = Math.max(...this.rows.map(row => row.items.length));
      this.rows.forEach((row, i) => {
        const value = metrics[i];
        row.width = value.width;
        row.columns = value.columns;
        row.step = value.width + value.gap;
        row.track.querySelectorAll('[data-carousel-spacer]').forEach(node => node.remove());
        for (let n = row.items.length; n < maxSlots; n++) {
          const spacer = document.createElement('li');
          spacer.className = 'lm-showcase__spacer';
          spacer.setAttribute('aria-hidden', 'true');
          spacer.setAttribute('role', 'presentation');
          spacer.setAttribute('data-carousel-spacer', '');
          row.track.append(spacer);
        }
        row.hasOverflow = maxSlots > row.columns;
        row.firstPassDistancePx = Math.max(0, (maxSlots - row.columns) * row.step);
        const targetMs = Math.max(1, Number(this.root.dataset.passTarget) || 90000);
        const minimumDwellMs = Math.max(0, Number(this.root.dataset.minCardDwellMs) || 0);
        const readableMs = minimumDwellMs && row.rail.clientWidth > row.width ? row.firstPassDistancePx / (row.rail.clientWidth - row.width) * minimumDwellMs : 0;
        row.durationMs = Math.max(targetMs, readableMs);
        row.speedPxSec = row.firstPassDistancePx ? row.firstPassDistancePx / row.durationMs * 1000 : 0;
      });
      this.resetPass();
      this.sync();
    }
    currentOffset(row) {
      if (!row.animation) return row.rail.scrollLeft;
      const progress = Math.max(0, Math.min(1, (Number(row.animation.currentTime) || 0) / row.segmentDuration));
      return row.segmentFrom + (row.step - row.segmentFrom) * progress;
    }
    accrue(row) {
      if (!row.animation) return;
      const time = Math.max(0, Math.min(row.segmentDuration, Number(row.animation.currentTime) || 0));
      const elapsed = Math.max(0, time - row.accountedTime);
      row.activeMs += elapsed;
      row.passActiveMs += elapsed;
      row.distancePx += elapsed / 1000 * row.speedPxSec;
      row.accountedTime = time;
    }
    commit(row) {
      if (!row.animation) return;
      this.accrue(row);
      const position = row.rail.scrollLeft + this.currentOffset(row);
      const animation = row.animation;
      row.animation = null;
      animation.cancel();
      row.rail.scrollTo({ left: position, behavior: 'instant' });
      row.offset = row.rail.scrollLeft;
      row.accountedTime = 0;
    }
    commitAll() { this.rows?.forEach(row => this.commit(row)); }
    resetPass() {
      this.commitAll();
      this.firstPassComplete = false;
      this.passGeneration++;
      this.rows.forEach(row => {
        const left = row.rail.scrollLeft;
        const rotate = row.step > 0 ? Math.floor(left / row.step) : 0;
        for (let i = 0; i < rotate; i++) row.track.append(row.track.firstElementChild);
        row.rail.scrollTo({ left: Math.max(0, left - rotate * row.step), behavior: 'instant' });
        row.offset = row.rail.scrollLeft;
        row.seen.clear();
        row.firstPassComplete = false;
        row.passActiveMs = 0;
        row.distancePx = 0;
        row.strides = 0;
      });
      this.manualDirty = false;
      this.root.dataset.passGeneration = String(this.passGeneration);
      this.root.dataset.passCompleted = 'false';
    }
    imageBandVisible(row) {
      const media = row.container.querySelector('.lm-showcase__media');
      if (!media) return false;
      const rect = media.getBoundingClientRect();
      return rect.width > 0 && rect.height > 0 && rect.top >= 0 && rect.bottom <= innerHeight;
    }
    recordVisible(row) {
      if (row.firstPassComplete || !this.imageBandVisible(row)) return;
      const rail = row.rail.getBoundingClientRect();
      row.items.forEach(item => {
        const box = item.querySelector('.lm-showcase__media')?.getBoundingClientRect();
        if (box && box.left >= rail.left - 0.8 && box.right <= rail.right + 0.8) row.seen.add(item.dataset.productId);
      });
      if (row.seen.size === row.items.length) row.firstPassComplete = true;
    }
    scheduleVisibility() {
      if (this.visibilityFrame) return;
      this.visibilityFrame = requestAnimationFrame(() => { this.visibilityFrame = null; this.sync(); });
    }
    revealFocus(target) {
      const rail = target.closest('[data-carousel-rail]');
      if (!rail || target === rail) return;
      const bounds = target.getBoundingClientRect();
      const viewport = rail.getBoundingClientRect();
      const distance = bounds.left < viewport.left ? bounds.left - viewport.left : bounds.right > viewport.right ? bounds.right - viewport.right : 0;
      if (Math.abs(distance) > 0.5) rail.scrollBy({ left: distance, behavior: 'instant' });
    }
    clearInteraction() {
      clearTimeout(this.interactionTimer);
      this.interactionTimer = null;
      this.interaction = null;
    }
    settleInteraction() {
      clearTimeout(this.interactionTimer);
      if (!this.interaction || this.interaction.active) return;
      this.interactionTimer = setTimeout(() => { this.clearInteraction(); this.sync(); }, 250);
    }
    yieldManual(row) {
      if (this.interaction?.row !== row) {
        this.clearInteraction();
        this.interaction = { row, active: false };
      }
      this.interaction.horizontal = true;
      this.interaction.vertical = false;
      this.commitAll();
      this.manualDirty = true;
      this.sync();
      this.settleInteraction();
    }
    move(row, direction) {
      this.yieldManual(row);
      row.rail.scrollBy({ left: direction * row.columns * row.step, behavior: this.motion.matches ? 'instant' : 'smooth' });
    }
    baseState() {
      return this.editor ? 'editor' : this.motion.matches ? 'reduced' : !this.canAnimate ? 'static' : this.userPaused ? 'paused' : !this.pageActive || document.visibilityState !== 'visible' ? 'hidden' : this.focused ? 'focus' : this.interaction && !this.interaction.vertical ? 'interacting' : document.querySelector('dialog[data-preview-dialog][open]') ? 'dialog' : 'running';
    }
    sync() {
      if (!this.ready || this.syncing) return;
      this.syncing = true;
      const base = this.baseState();
      const overflow = this.rows.some(row => row.hasOverflow);
      if (base === 'running' && this.manualDirty) this.resetPass();
      this.rows.forEach(row => { this.accrue(row); if (base === 'running') this.recordVisible(row); });
      if (!this.firstPassComplete && this.rows.every(row => row.firstPassComplete)) {
        this.firstPassComplete = true;
        this.root.dataset.passCompleted = 'true';
      }
      let running = false;
      this.rows.forEach(row => {
        const visible = this.imageBandVisible(row);
        const state = base !== 'running' ? base : row.hovered ? 'hover' : !visible ? 'offscreen' : !row.hasOverflow ? 'static' : row.firstPassComplete && !this.firstPassComplete ? 'barrier' : 'running';
        row.state = state;
        row.container.dataset.motionState = state;
        row.container.dataset.rowVisible = String(visible);
        if (state === 'running') {
          running = true;
          if (row.animation) row.animation.play();
          else this.startStride(row);
        } else if (row.animation) {
          const time = row.animation.currentTime;
          row.animation.pause();
          row.animation.currentTime = time;
        }
        row.progress?.setAttribute('aria-live', state === 'running' ? 'off' : 'polite');
        this.diagnostics(row);
      });
      this.root.dataset.motionState = base !== 'running' ? base : !overflow ? 'static' : running ? 'running' : this.rows.some(row => row.state === 'hover') ? 'hover' : this.rows.some(row => row.state === 'barrier') ? 'barrier' : 'offscreen';
      const hasMotion = overflow || lifestyleMotion.has(this.root);
      if (this.controls) this.controls.hidden = !hasMotion || this.motion.matches || this.editor || !this.canAnimate;
      if (this.button) {
        this.button.disabled = !hasMotion || this.motion.matches || this.editor || !this.canAnimate;
        this.button.setAttribute('aria-pressed', String(this.userPaused));
        this.button.textContent = this.userPaused ? this.root.dataset.playLabel || 'Play motion' : this.root.dataset.pauseLabel || 'Pause motion';
      }
      this.updateControls();
      lifestyleMotion.sync(this.root);
      this.syncing = false;
    }
    startStride(row) {
      if (!row.speedPxSec || typeof row.track.animate !== 'function') return;
      const from = Math.max(0, Math.min(row.step, row.rail.scrollLeft));
      row.segmentFrom = from;
      row.segmentDuration = Math.max(1, (row.step - from) / row.speedPxSec * 1000);
      row.accountedTime = 0;
      const animation = row.track.animate([{ transform: `translate3d(${-from}px,0,0)` }, { transform: `translate3d(${-row.step}px,0,0)` }], { duration: row.segmentDuration, easing: 'linear', fill: 'forwards' });
      row.animation = animation;
      row.rail.scrollTo({ left: 0, behavior: 'instant' });
      animation.finished.then(() => this.finishStride(row, animation)).catch(() => {});
    }
    finishStride(row, animation) {
      if (!this.ready || row.animation !== animation) return;
      this.accrue(row);
      const first = row.track.firstElementChild;
      row.animation = null;
      row.track.append(first);
      animation.cancel();
      row.rail.scrollTo({ left: 0, behavior: 'instant' });
      row.offset = 0;
      row.accountedTime = 0;
      row.strides++;
      if (this.baseState() === 'running') this.recordVisible(row);
      this.sync();
    }
    diagnostics(row) {
      row.container.dataset.rowActiveMs = String(Math.round(row.activeMs));
      row.container.dataset.passActiveMs = String(Math.round(row.passActiveMs));
      row.container.dataset.distancePx = String(Math.round(row.distancePx * 100) / 100);
      row.container.dataset.speedPxSec = String(Math.round(row.speedPxSec * 1000) / 1000);
      row.container.dataset.passDuration = String(Math.round(row.durationMs));
      row.container.dataset.passSeen = String(row.seen.size);
      row.container.dataset.passTotal = String(row.items.length);
      row.container.dataset.passComplete = String(row.firstPassComplete);
    }
    updateControls() {
      this.rows.forEach(row => {
        const maximum = row.rail.scrollWidth - row.rail.clientWidth;
        if (row.previous) row.previous.disabled = row.rail.scrollLeft <= 2;
        if (row.next) row.next.disabled = row.rail.scrollLeft >= maximum - 2;
        const total = row.items.length;
        const text = (this.root.dataset.designsLabel || '[total] designs').replace('[total]', String(total));
        if (row.progress && row.progress.textContent !== text) row.progress.textContent = text;
      });
    }
  }

  class ProductCarousel extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      if (document.readyState === 'loading') { this.waitForContent(); return; }
      if (this.hasAttribute('data-collection-pass')) {
        this.collectionPass ||= new CollectionPass(this);
        this.collectionPass.connect();
        return;
      }
      this.querySelectorAll('[data-carousel-clone]').forEach(clone => clone.remove());
      this.rows = [...this.querySelectorAll('[data-carousel-row]')].map(container => {
        const rail = container.querySelector('[data-carousel-rail]');
        const track = rail?.querySelector('[data-carousel-track]');
        if (!rail || !track) return null;
        const row = { container, rail, track, items: [...track.querySelectorAll('[data-carousel-item]')], previous: container.querySelector('[data-carousel-prev]'), next: container.querySelector('[data-carousel-next]'), progress: container.querySelector('[data-carousel-progress]'), direction: 1, staticPosition: rail.scrollLeft, overflow: false, animation: null };
        Object.defineProperty(row, 'position', { get: () => this.positionOf(row), set: value => {
          row.staticPosition = Number(value) || 0;
          if (row.animation) row.animation.currentTime = this.timeForPosition(row, row.staticPosition);
        } });
        return row;
      }).filter(Boolean);
      if (!this.rows.length) { this.waitForContent(); return; }
      this.childObserver?.disconnect();
      this.childEvents?.abort();
      clearTimeout(this.childTimeout);
      this.childObserver = null;
      this.ready = true;
      this.events = new AbortController();
      const signal = this.events.signal;
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.canAnimate = this.rows.every(row => typeof row.track.animate === 'function');
      this.editorMode = Boolean(window.Shopify?.designMode);
      this.motionButton = this.querySelector('[data-carousel-motion]');
      this.motionControls = this.querySelector('[data-motion-controls]');
      this.userPaused = this.motion.matches || Boolean(navigator.connection?.saveData);
      this.visible = false;
      this.imagePageActive = true;
      this.running = false;
      this.configureLoops();
      this.querySelectorAll('.lm-showcase__media img').forEach(image => this.prepareImage(image));
      this.rows.forEach(row => {
        const controls = row.container.querySelector('[data-carousel-controls]');
        if (controls) controls.hidden = false;
        row.previous?.addEventListener('click', () => this.move(row, -1), { signal });
        row.next?.addEventListener('click', () => this.move(row, 1), { signal });
        row.rail.addEventListener('keydown', event => {
          if (event.target !== row.rail || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
          event.preventDefault();
          this.pause();
          if (event.key === 'Home' || event.key === 'End') row.rail.scrollTo({ left: event.key === 'Home' ? 0 : row.rail.scrollWidth, behavior: this.motion.matches ? 'instant' : 'smooth' });
          else this.move(row, event.key === 'ArrowRight' ? 1 : -1);
        }, { signal });
        row.rail.addEventListener('scroll', () => {
          if (this.running && row.rail.scrollLeft > 1) this.pause();
          if (!this.running) {
            row.staticPosition = row.rail.scrollLeft;
            this.scheduleUpdate();
          }
        }, { passive: true, signal });
      });
      const pauseForInteraction = event => {
        if (event.target instanceof Element && event.target.closest('[data-carousel-motion]')) return;
        this.pause();
      };
      this.addEventListener('pointerover', pauseForInteraction, { signal });
      this.addEventListener('pointerdown', pauseForInteraction, { passive: true, signal });
      this.addEventListener('touchstart', pauseForInteraction, { passive: true, signal });
      this.addEventListener('wheel', () => this.pause(), { passive: true, signal });
      this.addEventListener('focusin', pauseForInteraction, { signal });
      this.motionButton?.addEventListener('click', () => {
        if (this.motion.matches || this.editorMode) return;
        this.userPaused = !this.userPaused;
        this.syncMotion();
      }, { signal });
      this.motion.addEventListener('change', () => {
        if (this.motion.matches) this.userPaused = true;
        this.stopMotion();
        this.configureLoops();
        this.scheduleUpdate();
      }, { signal });
      window.addEventListener('resize', () => this.scheduleUpdate(), { passive: true, signal });
      document.addEventListener('visibilitychange', () => this.syncMotion(), { signal });
      window.addEventListener('pagehide', () => { this.imagePageActive = false; this.stopMotion(); lifestyleMotion.sync(this); }, { signal });
      window.addEventListener('pageshow', () => { this.imagePageActive = true; this.scheduleUpdate(); }, { signal });
      if (typeof ResizeObserver !== 'undefined') {
        this.observer = new ResizeObserver(() => this.scheduleUpdate());
        this.rows.forEach(row => this.observer.observe(row.rail));
      }
      if (typeof IntersectionObserver !== 'undefined') {
        this.visibilityObserver = new IntersectionObserver(entries => {
          this.visible = entries.some(entry => entry.isIntersecting && entry.intersectionRatio >= 0.1);
          this.syncMotion();
        }, { threshold: [0, 0.1] });
        this.visibilityObserver.observe(this);
      }
      this.dialogObserver = new MutationObserver(records => {
        if (records.some(record => record.target.matches('dialog[data-preview-dialog]'))) this.syncMotion();
      });
      this.dialogObserver.observe(document.documentElement, { subtree: true, attributes: true, attributeFilter: ['open'] });
      lifestyleMotion.register(this, this.rows, () => this.ready && !this.editorMode && !this.motion.matches && this.canAnimate && !this.userPaused && this.imagePageActive && document.visibilityState === 'visible' && this.visible && !document.querySelector('dialog[data-preview-dialog][open]'));
      this.scheduleUpdate();
    }
    disconnectedCallback() {
      if (this.collectionPass) { this.collectionPass.disconnect(); return; }
      lifestyleMotion.unregister(this);
      this.childObserver?.disconnect();
      this.childEvents?.abort();
      clearTimeout(this.childTimeout);
      this.childObserver = null;
      this.events?.abort();
      this.observer?.disconnect();
      this.visibilityObserver?.disconnect();
      this.dialogObserver?.disconnect();
      this.stopMotion();
      this.rows?.forEach(row => row.track.querySelectorAll('[data-carousel-clone]').forEach(clone => clone.remove()));
      this.ready = false;
    }
    waitForContent() {
      if (this.childObserver || !this.isConnected) return;
      this.childEvents?.abort();
      this.childEvents = new AbortController();
      const tryReady = () => {
        if (document.readyState !== 'loading' && this.querySelector('[data-carousel-row] [data-carousel-track]')) this.connectedCallback();
      };
      document.addEventListener('DOMContentLoaded', tryReady, { once: true, signal: this.childEvents.signal });
      this.childObserver = new MutationObserver(tryReady);
      this.childObserver.observe(this, { childList: true, subtree: true });
      this.childTimeout = setTimeout(() => { this.childObserver?.disconnect(); this.childObserver = null; }, 10000);
    }
    prepareImage(image) {
      if (image.hasAttribute('data-showcase-lifestyle-overlay')) return;
      const fallback = () => {
        image.hidden = true;
        const message = image.parentElement.querySelector('[data-showcase-image-missing]');
        if (message) message.hidden = false;
      };
      image.addEventListener('error', fallback, { signal: this.events.signal });
      if (image.complete && !image.naturalWidth) fallback();
    }
    configureLoops() {
      this.rows.forEach(row => {
        const loop = row.items.length >= 5 && !this.motion.matches && !this.editorMode && this.canAnimate && !this.hasAttribute('data-carousel-no-clones');
        if (loop && !row.loop) {
          const cloneGroup = () => row.items.map(item => {
            const clone = item.cloneNode(true);
            clone.setAttribute('data-carousel-clone', '');
            clone.setAttribute('aria-hidden', 'true');
            [clone, ...clone.querySelectorAll('*')].forEach(element => {
              ['id', 'aria-labelledby', 'aria-describedby', 'for', 'headers', 'data-showcase-card', 'data-carousel-item', 'data-shopify-editor-block'].forEach(attribute => element.removeAttribute(attribute));
              if (element.matches('a, button, input, select, textarea, [tabindex], [contenteditable]')) element.setAttribute('tabindex', '-1');
              if (element.hasAttribute('contenteditable')) element.setAttribute('contenteditable', 'false');
            });
            clone.querySelectorAll('.lm-showcase__media img').forEach(image => this.prepareImage(image));
            return clone;
          });
          row.track.prepend(...cloneGroup());
          row.track.append(...cloneGroup(), ...cloneGroup());
          row.loop = true;
          row.cycle = 0;
        } else if (!loop && row.loop) {
          const offset = row.cycle ? ((row.rail.scrollLeft - row.start) % row.cycle + row.cycle) % row.cycle : 0;
          row.track.querySelectorAll('[data-carousel-clone]').forEach(clone => clone.remove());
          row.loop = false;
          row.cycle = 0;
          row.rail.scrollTo({ left: offset, behavior: 'instant' });
          row.staticPosition = row.rail.scrollLeft;
        }
      });
    }
    positionOf(row) {
      if (!row.animation) return row.staticPosition;
      const time = Number(row.animation.currentTime) || 0;
      const duration = row.duration;
      if (row.loop) return row.start + (time % duration) * 0.02;
      const odd = Math.floor(time / duration) % 2 === 1;
      const distance = (time % duration) * 0.02;
      return odd ? row.maximum - distance : distance;
    }
    timeForPosition(row, position) {
      if (row.loop) return ((position - row.start) % row.cycle + row.cycle) % row.cycle / 0.02;
      const distance = Math.max(0, Math.min(row.maximum, position));
      return row.direction === -1 ? row.duration + (row.maximum - distance) / 0.02 : distance / 0.02;
    }
    pause() {
      this.userPaused = true;
      this.syncMotion();
    }
    move(row, direction) {
      this.pause();
      row.rail.scrollBy({ left: direction * row.rail.clientWidth * 0.82, behavior: this.motion.matches ? 'instant' : 'smooth' });
    }
    scheduleUpdate() {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        if (!this.ready || !this.isConnected) return;
        this.stopMotion();
        this.updateRows();
        this.syncMotion();
      });
    }
    stopMotion() {
      this.running = false;
      cancelAnimationFrame(this.frame);
      this.frame = null;
      this.rows?.forEach(row => {
        if (row.animation) {
          const position = this.positionOf(row) + row.rail.scrollLeft;
          if (!row.loop) row.direction = Math.floor((Number(row.animation.currentTime) || 0) / row.duration) % 2 ? -1 : 1;
          row.animation.cancel();
          row.animation = null;
          row.rail.scrollTo({ left: position, behavior: 'instant' });
          row.staticPosition = row.rail.scrollLeft;
        }
        row.rail.classList.remove('is-auto-moving');
        delete row.rail.dataset.autoMoving;
        row.progress?.setAttribute('aria-live', 'polite');
      });
    }
    syncMotion() {
      if (!this.ready) return;
      const overflow = this.rows.some(row => row.overflow);
      const state = this.editorMode ? 'editor' : this.motion.matches ? 'reduced' : !overflow || !this.canAnimate ? 'static' : this.userPaused ? 'paused' : document.visibilityState !== 'visible' ? 'hidden' : !this.visible ? 'offscreen' : document.querySelector('dialog[data-preview-dialog][open]') ? 'dialog' : 'running';
      this.dataset.motionState = state;
      const hasMotion = overflow || lifestyleMotion.has(this);
      if (this.motionControls) this.motionControls.hidden = !hasMotion || this.motion.matches || this.editorMode || !this.canAnimate;
      if (this.motionButton) {
        this.motionButton.disabled = !hasMotion || this.motion.matches || this.editorMode || !this.canAnimate;
        this.motionButton.setAttribute('aria-pressed', String(this.userPaused));
        this.motionButton.textContent = this.userPaused ? this.dataset.playLabel || 'Play motion' : this.dataset.pauseLabel || 'Pause motion';
      }
      lifestyleMotion.sync(this);
      if (state !== 'running') { this.stopMotion(); this.updateControls(); return; }
      this.running = true;
      this.rows.forEach(row => {
        row.rail.classList.toggle('is-auto-moving', row.overflow);
        row.progress?.setAttribute('aria-live', row.overflow ? 'off' : 'polite');
        if (!row.overflow) return;
        row.rail.dataset.autoMoving = 'true';
        if (row.animation) return;
        const position = row.rail.scrollLeft;
        row.duration = (row.loop ? row.cycle : row.maximum) / 0.02;
        if (!Number.isFinite(row.duration) || row.duration <= 0) return;
        row.animation = row.track.animate([
          { transform: `translate3d(-${row.loop ? row.start : 0}px,0,0)` },
          { transform: `translate3d(-${row.loop ? row.start + row.cycle : row.maximum}px,0,0)` }
        ], { duration: row.duration, iterations: Infinity, easing: 'linear', direction: row.loop ? 'normal' : 'alternate', fill: 'both' });
        row.animation.currentTime = this.timeForPosition(row, position);
        row.rail.scrollTo({ left: 0, behavior: 'instant' });
      });
      this.updateControls();
    }
    updateRows() {
      this.rows.forEach(row => {
        if (row.loop) {
          const first = row.items[0].getBoundingClientRect();
          const after = row.items[row.items.length - 1].nextElementSibling?.getBoundingClientRect();
          const start = first.left - row.rail.getBoundingClientRect().left + row.rail.scrollLeft;
          const cycle = after ? after.left - first.left : 0;
          if (cycle > 0 && Math.abs(cycle - row.cycle) > 0.5) {
            const phase = row.cycle > 0 ? ((row.staticPosition - row.start) % row.cycle + row.cycle) % row.cycle / row.cycle : 0;
            row.rail.scrollTo({ left: start + phase * cycle, behavior: 'instant' });
          }
          row.start = start;
          row.cycle = cycle;
        }
        row.maximum = row.rail.scrollWidth - row.rail.clientWidth;
        row.overflow = row.maximum > 2;
        row.staticPosition = row.rail.scrollLeft;
      });
      this.updateControls();
    }
    updateControls() {
      this.rows.forEach(row => {
        const position = this.positionOf(row);
        if (row.previous) row.previous.disabled = !row.overflow || (!row.animation && position <= 2);
        if (row.next) row.next.disabled = !row.overflow || (!row.animation && position >= row.maximum - 2);
        if (!row.progress) return;
        if (row.loop || row.animation) {
          const text = (this.dataset.loopPositionLabel || '[total] designs').replace('[total]', String(row.items.length));
          if (row.progress.textContent !== text) row.progress.textContent = text;
          return;
        }
        const viewport = row.rail.getBoundingClientRect();
        const visible = row.items.map((item, index) => ({ bounds: item.getBoundingClientRect(), index })).filter(({ bounds }) => bounds.right > viewport.left + 2 && bounds.left < viewport.right - 2);
        const start = visible.length ? visible[0].index + 1 : 0;
        const end = visible.length ? visible[visible.length - 1].index + 1 : 0;
        const text = (this.dataset.positionLabel || 'Showing [start]–[end] of [total]').replace('[start]', String(start)).replace('[end]', String(end)).replace('[total]', String(row.items.length));
        if (row.progress.textContent !== text) row.progress.textContent = text;
      });
    }
  }
  customElements.define('lm-product-carousel', ProductCarousel);

  const prepareFeaturedImages = () => document.querySelectorAll('[data-featured-model-card] img').forEach(image => {
    if (image.dataset.fallbackReady) return;
    image.dataset.fallbackReady = 'true';
    const fallback = () => {
      image.hidden = true;
      const message = image.closest('[data-featured-model-card]')?.querySelector('[data-featured-image-missing]');
      if (message) message.hidden = false;
    };
    image.addEventListener('error', fallback, { once: true });
    if (image.complete && !image.naturalWidth) fallback();
  });
  prepareFeaturedImages();
  document.addEventListener('shopify:section:load', prepareFeaturedImages);

  // Reuse the tested breed dialog: one owner, one model, and its original image fallback.
  document.addEventListener('click', event => {
    const trigger = event.target instanceof Element ? event.target.closest('[data-open-featured-model]') : null;
    if (!trigger || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const discovery = document.querySelector('lm-dog-discovery');
    const feature = trigger.closest('[data-featured-model-card]');
    const id = trigger.dataset.productId;
    if (!feature || !/^\d+$/.test(id || '') || !discovery?.ready || typeof discovery.openPreview !== 'function' || typeof discovery.startModel !== 'function' || discovery.dialog?.open) return;
    const known = discovery.cards?.find(card => card.dataset.productId === id);
    if (!known?.dataset.model || feature.dataset.productId !== id) return;
    try {
      const expected = new URL(known.dataset.model, location.href).href;
      if (new URL(trigger.dataset.model, location.href).href !== expected || new URL(feature.dataset.model, location.href).href !== expected) return;
    } catch (_) { return; }
    event.preventDefault();
    discovery.openPreview(feature, trigger);
    discovery.startModel();
  });

  class VerifiedOffer extends HTMLElement {
    connectedCallback() {
      this.events?.abort();
      this.events = new AbortController();
      const signal = this.events.signal;
      window.addEventListener('pageshow', () => this.refresh(), { signal });
      document.addEventListener('visibilitychange', () => this.refresh(), { signal });
      this.refresh();
    }
    refresh() {
      clearTimeout(this.expiry);
      const start = Number(this.dataset.offerStart) * 1000;
      const end = Number(this.dataset.offerEnd) * 1000;
      const now = Date.now();
      const valid = this.dataset.offerState === 'ACTIVE' && Number.isFinite(start) && Number.isFinite(end) && start > 0 && end - start <= 86400000 && start <= now && now < end;
      this.hidden = !valid;
      if (valid) this.expiry = setTimeout(() => { this.hidden = true; }, Math.min(end - now + 25, 2147483647));
    }
    disconnectedCallback() { clearTimeout(this.expiry); this.events?.abort(); }
  }
  customElements.define('lm-verified-offer', VerifiedOffer);

  class VerifiedSpecials extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.sectionId = this.dataset.specialsSectionId || '';
      this.content = [...this.children].find(node => node.matches('[data-specials-content]'));
      if (!this.content || !/^[a-zA-Z0-9_-]{1,160}$/.test(this.sectionId)) { this.hidden = true; return; }
      this.ready = true;
      this.events = new AbortController();
      this.generation = (this.generation || 0) + 1;
      this.failures = 0;
      this.nextRefreshAt = 0;
      this.pageActive = true;
      this.inFlight = null;
      this.request = null;
      const signal = this.events.signal;
      this.anchor = this.closest('.shopify-section') || this.parentElement || this;
      this.revalidate();
      const resume = () => {
        this.revalidate();
        this.updateVisibility();
        this.schedulePoll();
      };
      document.addEventListener('visibilitychange', resume, { signal });
      window.addEventListener('pageshow', () => { this.pageActive = true; this.nextRefreshAt = 0; resume(); }, { signal });
      window.addEventListener('pagehide', () => { this.pageActive = false; this.suspend(); }, { signal });
      if (typeof IntersectionObserver !== 'undefined') {
        this.observer = new IntersectionObserver(entries => {
          this.inView = entries.some(entry => entry.isIntersecting);
          this.schedulePoll();
        });
        this.observer.observe(this.anchor);
      } else {
        const visibilityUpdate = () => {
          if (this.visibilityFrame) return;
          this.visibilityFrame = requestAnimationFrame(() => { this.visibilityFrame = null; this.updateVisibility(); this.schedulePoll(); });
        };
        window.addEventListener('scroll', visibilityUpdate, { passive: true, signal });
        window.addEventListener('resize', visibilityUpdate, { passive: true, signal });
      }
      this.updateVisibility();
      this.schedulePoll();
    }
    disconnectedCallback() {
      this.ready = false;
      this.generation = (this.generation || 0) + 1;
      this.events?.abort();
      this.observer?.disconnect();
      this.suspend();
      this.inFlight = null;
      this.request = null;
      clearTimeout(this.expiryTimer);
      cancelAnimationFrame(this.visibilityFrame);
      this.visibilityFrame = null;
    }
    updateVisibility() {
      const rect = this.anchor?.getBoundingClientRect();
      this.inView = Boolean(rect && rect.bottom >= 0 && rect.top <= innerHeight && rect.right >= 0 && rect.left <= innerWidth);
    }
    active() { return this.ready && this.pageActive && this.isConnected && this.inView && document.visibilityState === 'visible'; }
    suspend() {
      clearTimeout(this.pollTimer);
      this.pollTimer = null;
      this.request?.abort();
    }
    validCard(card, now = Date.now()) {
      const offer = card.querySelector('lm-verified-offer');
      const start = Number(offer?.dataset.offerStart) * 1000;
      const end = Number(offer?.dataset.offerEnd) * 1000;
      const variant = card.dataset.variantId || '';
      if (!offer || offer.dataset.offerState !== 'ACTIVE' || !/^\d+$/.test(card.dataset.productId || '') || !/^\d+$/.test(variant) || offer.dataset.offerVariant !== variant || card.dataset.variantState !== 'available' || !Number.isFinite(start) || !Number.isFinite(end) || start <= 0 || end <= start || end - start > 86400000 || start > now || now >= end) return false;
      try {
        const link = card.querySelector('[data-showcase-link]');
        if (!link) return false;
        const url = new URL(link.getAttribute('href'), location.href);
        return url.origin === location.origin && /\/products\/[^/]+$/.test(url.pathname) && url.searchParams.get('variant') === variant;
      } catch (_) { return false; }
    }
    prune(content) {
      const now = Date.now();
      let removed = 0;
      const seen = new Set();
      content.querySelectorAll('[data-carousel-clone]').forEach(clone => clone.remove());
      content.querySelectorAll('[data-specials-card]').forEach(card => {
        const id = card.dataset.variantId;
        if (!this.validCard(card, now) || seen.has(id)) { card.remove(); removed++; }
        else seen.add(id);
      });
      content.querySelectorAll('[data-carousel-row]').forEach(row => {
        if (!row.querySelector('[data-specials-card]')) row.remove();
      });
      const cards = [...content.querySelectorAll('[data-specials-card]')];
      if (!cards.length) content.replaceChildren();
      return { cards, removed };
    }
    signature(content) {
      const copy = content.cloneNode(true);
      copy.querySelectorAll('[data-carousel-clone]').forEach(clone => clone.remove());
      copy.querySelectorAll('[data-showcase-lifestyle-overlay], [data-showcase-lifestyle-disclosure]').forEach(node => node.remove());
      copy.querySelectorAll('*').forEach(node => {
        ['hidden', 'disabled', 'aria-pressed', 'data-motion-state', 'data-auto-moving', 'data-lifestyle-running', 'data-lifestyle-active'].forEach(attribute => node.removeAttribute(attribute));
        node.classList.remove('is-auto-moving');
      });
      copy.querySelectorAll('[data-carousel-progress], [data-carousel-motion]').forEach(node => { node.textContent = ''; node.removeAttribute('aria-live'); });
      return copy.innerHTML;
    }
    focusRecord() {
      const active = document.activeElement;
      if (!this.content.contains(active)) return null;
      return { href: active.closest('a[href]')?.getAttribute('href'), motion: active.matches('[data-carousel-motion]') };
    }
    restoreFocus(record) {
      if (!record) return;
      let target;
      if (!this.hidden) {
        if (record.href) target = [...this.content.querySelectorAll('a[href]')].find(link => link.getAttribute('href') === record.href);
        if (!target && record.motion) target = this.content.querySelector('[data-carousel-motion]:not([disabled])');
        if (!target) target = this.content.querySelector('[data-specials-card] [data-showcase-link]');
      }
      if (!target) {
        const visible = [...document.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex="0"]')].filter(node => !this.contains(node) && node.getClientRects().length && !node.closest('[hidden], [inert], [aria-hidden="true"]'));
        const next = visible.filter(node => this.compareDocumentPosition(node) & Node.DOCUMENT_POSITION_FOLLOWING);
        target = next.find(node => { const rect = node.getBoundingClientRect(); return rect.top >= 0 && rect.top < innerHeight; }) || next[0] || visible[0];
      }
      if (target) {
        target.focus({ preventScroll: true });
        if (this.contains(target)) target.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'instant' });
      } else {
        const main = document.querySelector('main, #MainContent');
        if (main) {
          const previous = main.getAttribute('tabindex');
          main.setAttribute('tabindex', '-1');
          main.focus({ preventScroll: true });
          main.addEventListener('blur', () => { if (previous === null) main.removeAttribute('tabindex'); else main.setAttribute('tabindex', previous); }, { once: true });
        }
      }
    }
    replaceContent(incoming) {
      const focus = this.focusRecord();
      const paused = [...this.content.querySelectorAll('lm-product-carousel')].some(carousel => carousel.userPaused);
      this.content.replaceChildren(...incoming.childNodes);
      this.hidden = !this.content.querySelector('[data-specials-card]');
      this.content.querySelectorAll('lm-product-carousel').forEach(carousel => {
        if (paused) carousel.userPaused = true;
        carousel.scheduleUpdate?.();
      });
      this.restoreFocus(focus);
    }
    revalidate() {
      if (!this.ready || !this.content) return;
      clearTimeout(this.expiryTimer);
      const incoming = this.content.cloneNode(true);
      const { cards, removed } = this.prune(incoming);
      if (removed || (!cards.length && this.content.childNodes.length)) this.replaceContent(incoming);
      this.hidden = !cards.length;
      if (!this.hidden) this.content.querySelectorAll('lm-product-carousel').forEach(carousel => carousel.scheduleUpdate?.());
      this.lastSignature = this.signature(this.content);
      if (cards.length) {
        const end = Math.min(...cards.map(card => Number(card.querySelector('lm-verified-offer').dataset.offerEnd) * 1000));
        this.expiryTimer = setTimeout(() => {
          this.revalidate();
          this.nextRefreshAt = 0;
          this.updateVisibility();
          this.schedulePoll();
        }, Math.min(Math.max(0, end - Date.now() + 25), 2147483647));
      }
    }
    schedulePoll() {
      clearTimeout(this.pollTimer);
      this.pollTimer = null;
      if (!this.active()) { this.request?.abort(); return; }
      if (this.inFlight) return;
      this.pollTimer = setTimeout(() => { this.pollTimer = null; this.refreshFromServer(); }, Math.max(0, this.nextRefreshAt - Date.now()));
    }
    async refreshFromServer() {
      if (this.inFlight || !this.active()) return this.inFlight;
      const generation = this.generation;
      const controller = new AbortController();
      this.request = controller;
      this.nextRefreshAt = Date.now() + 60000;
      const timeout = setTimeout(() => controller.abort(), 8000);
      this.inFlight = (async () => {
        const url = new URL(location.href);
        url.hash = '';
        url.searchParams.delete('sections');
        url.searchParams.delete('sections[]');
        url.searchParams.set('section_id', this.sectionId);
        try {
          const response = await fetch(url.href, { method: 'GET', credentials: 'same-origin', mode: 'same-origin', redirect: 'error', cache: 'no-store', signal: controller.signal, headers: { Accept: 'text/html' } });
          if (!response.ok || new URL(response.url || url.href).origin !== location.origin) throw new Error('Specials unavailable');
          const html = await response.text();
          if (html.length > 1000000) throw new Error('Unexpected section size');
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const matches = [...doc.querySelectorAll('lm-verified-specials[data-specials-section-id]')].filter(node => node.dataset.specialsSectionId === this.sectionId);
          if (matches.length !== 1) throw new Error('Specials section mismatch');
          const incoming = [...matches[0].children].find(node => node.matches('[data-specials-content]'));
          if (!incoming) throw new Error('Specials content missing');
          incoming.querySelectorAll('script, iframe, object, embed, base, meta, link').forEach(node => node.remove());
          incoming.querySelectorAll('*').forEach(node => [...node.attributes].forEach(attribute => { if (/^on/i.test(attribute.name)) node.removeAttribute(attribute.name); }));
          this.prune(incoming);
          if (generation !== this.generation || !this.active() || controller.signal.aborted) return;
          const signature = this.signature(incoming);
          if (signature !== this.lastSignature) this.replaceContent(incoming);
          this.failures = 0;
          this.revalidate();
        } catch (_) {
          if (generation !== this.generation || !this.ready) return;
          this.revalidate();
          if (this.active()) {
            this.failures = Math.min(this.failures + 1, 4);
            this.nextRefreshAt = Date.now() + Math.min(60000 * 2 ** (this.failures - 1), 300000);
          }
        } finally {
          clearTimeout(timeout);
          if (generation === this.generation) {
            this.request = null;
            this.inFlight = null;
            this.schedulePoll();
          }
        }
      })();
      return this.inFlight;
    }
  }
  customElements.define('lm-verified-specials', VerifiedSpecials);
})();
